import React from 'react';

type SkeletonBlockProps = React.HTMLAttributes<HTMLDivElement>;

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

interface NamedSkeletonProps {
  label: string;
  className?: string;
}

interface SkeletonTableProps extends NamedSkeletonProps {
  rows?: number;
  columns?: number;
}

export const SkeletonBlock: React.FC<SkeletonBlockProps> = ({ className = '', ...props }) => (
  <div
    {...props}
    aria-hidden="true"
    className={`animate-pulse rounded bg-gray-200 motion-reduce:animate-none dark:bg-gray-700 ${className}`}
  />
);

export const SkeletonText: React.FC<SkeletonTextProps> = ({ lines = 3, className = '' }) => (
  <div aria-hidden="true" className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }, (_, index) => (
      <SkeletonBlock key={index} className={`h-4 ${index === lines - 1 ? 'w-3/4' : 'w-full'}`} />
    ))}
  </div>
);

export const SkeletonCard: React.FC<NamedSkeletonProps> = ({ label, className = '' }) => (
  <div
    role="status"
    aria-label={label}
    aria-live="polite"
    className={`rounded-lg border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900 ${className}`}
  >
    <SkeletonBlock className="mb-4 h-6 w-2/5" />
    <SkeletonText />
  </div>
);

export const SkeletonTable: React.FC<SkeletonTableProps> = ({
  label,
  rows = 5,
  columns = 4,
  className = '',
}) => (
  <div role="status" aria-label={label} aria-live="polite" className={`overflow-hidden ${className}`}>
    <div aria-hidden="true" className="space-y-3">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {Array.from({ length: columns }, (_, column) => (
            <SkeletonBlock key={column} className="h-5" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const SkeletonDetail: React.FC<NamedSkeletonProps> = ({ label, className = '' }) => (
  <div role="status" aria-label={label} aria-live="polite" className={`space-y-6 ${className}`}>
    <SkeletonBlock className="h-8 w-1/3" />
    <div aria-hidden="true" className="grid gap-4 sm:grid-cols-2">
      <div className="rounded-lg border border-gray-200 p-5 dark:border-gray-800">
        <SkeletonBlock className="mb-4 h-6 w-2/5" />
        <SkeletonText />
      </div>
      <div className="rounded-lg border border-gray-200 p-5 dark:border-gray-800">
        <SkeletonBlock className="mb-4 h-6 w-2/5" />
        <SkeletonText />
      </div>
    </div>
    <SkeletonText lines={4} />
  </div>
);
